// PAGOS Y ADEUDOS DEL MAESTRO — espejo de la pestaña «Pagos y adeudos» de la ficha de alumno
// (Carlos 29-sep-2026: «con los profesores no me está mostrando su deuda en ninguna parte»).
// Lo que un maestro debe son FIADOS de vitrina (`letsmove_debts.payer_user_id`); lo que ha pagado,
// sus compras y los abonos a esos fiados (`letsmove_payments.payer_user_id`). Aquí se cobra o se
// cancela el adeudo; el fiado nuevo se hace desde la Caja (Venta de vitrina → Maestro → Fiar).
import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, CreditCard, ShoppingBag, XCircle } from 'lucide-react';
import { getStaffDebts, cancelDebt, Debt, money, TYPE_LABEL, TYPE_COLOR, sinPrefijoVitrina } from '../../finances/api';
import { getTeacherPayments, TeacherPayment } from '../api';
import QuickPay from '../../finances/components/QuickPay';
import MetodoPago from '../../finances/components/MetodoPago';
import { Skeleton } from '../../../components/Skeleton';
import { useToast } from '../../../context/ToastContext';

/** Las compras viejas importadas de Kydemy vienen como «other» con concepto «AyB»: son vitrina
 *  (mismo criterio que el filtro de la ficha de alumno). */
const tipoDe = (p: TeacherPayment) => (p.type === 'other' && /a\s*y\s*b|a&b/i.test(p.assoc_name || '') ? 'product' : p.type);
const fecha = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(String(s).length <= 10 ? `${s}T12:00:00` : s);
  return isNaN(d.getTime()) ? String(s) : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function TeacherPagos({ userId, isAdmin }: { userId: number; isAdmin: boolean }) {
  const toast = useToast();
  const [debts, setDebts] = useState<Debt[] | null>(null);
  const [cancelados, setCancelados] = useState<any[]>([]);
  const [pays, setPays] = useState<TeacherPayment[] | null>(null);
  const [cancelando, setCancelando] = useState<number | null>(null);
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    getStaffDebts(userId).then((r) => setDebts(r.data.filter((d) => !d.settled))).catch(() => setDebts([]));
    getStaffDebts(userId, true).then((r) => setCancelados(r.data.filter((d: any) => d.cancelled_at))).catch(() => {});
    getTeacherPayments(userId).then((r) => setPays(r.data)).catch(() => setPays([]));
  }, [userId]);
  useEffect(load, [load]);

  const debe = (debts || []).reduce((s, d) => s + Number(d.remaining ?? d.amount ?? 0), 0);

  async function cancelar(debtId: number) {
    const m = motivo.trim();
    if (!m) { toast.error('Escribe el motivo'); return; }
    setSaving(true);
    try { await cancelDebt(debtId, m); toast.success('Adeudo cancelado'); setCancelando(null); setMotivo(''); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo cancelar el adeudo'); }
    finally { setSaving(false); }
  }

  return (
    <div className="mt-3 rounded-2xl bg-surface p-4 shadow-sm" data-testid="profe-pagos">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-line pb-1.5">
        <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-content">
          <CreditCard size={13} className="text-accent-ink" /> Pagos y adeudos
        </h3>
        {/* El fiado nuevo nace en la Caja, con el carrito: aquí solo se cobra o se cancela. */}
        <span className="text-[11px] text-content-3">Para fiarle vitrina: Caja › Venta de vitrina › Maestro › Fiar</span>
      </div>

      {/* ── Adeudos pendientes (se le acumulan; la caja puede cerrar sin cobrarlos) ── */}
      {debts === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : debts.length === 0 ? (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-2.5 text-sm text-emerald-700 dark:text-emerald-400" data-testid="profe-al-dia">
          <CheckCircle2 size={15} /> Al corriente: no debe nada de vitrina.
        </div>
      ) : (
        <div className="mb-4 overflow-hidden rounded-xl border border-red-500/30" data-testid="profe-adeudos">
          <div className="flex items-center justify-between bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-700 dark:text-red-400">
            <span className="flex items-center gap-1.5"><AlertCircle size={15} /> Adeudos pendientes</span>
            <span className="text-xs font-medium uppercase tracking-wide">Total <b className="ml-1 text-sm tabular-nums">{money(debe)}</b></span>
          </div>
          <ul className="divide-y divide-line/60">
            {debts.map((d) => {
              const resta = Number(d.remaining ?? d.amount);
              const abonado = Number(d.paid_amount || 0);
              const etiqueta = (d.debt_type === 'product' ? sinPrefijoVitrina(d.comment) : d.comment) || d.membership_name || 'Adeudo';
              return (
                <li key={d.debt_id} className="px-4 py-2.5" data-testid="profe-adeudo">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {d.debt_type && (
                          <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${TYPE_COLOR[d.debt_type] || TYPE_COLOR.other}`}>
                            {TYPE_LABEL[d.debt_type] || d.debt_type}
                          </span>
                        )}
                        <span className="truncate text-sm text-content">{etiqueta}</span>
                      </div>
                      <div className="mt-0.5 text-xs text-content-3">
                        fiado el {fecha(d.created_at)}{d.created_by_name ? ` · por ${d.created_by_name.split(' ')[0]}` : ''}
                        {abonado > 0 && <> · abonó {money(abonado)} de {money(Number(d.amount))}</>}
                      </div>
                    </div>
                    <span className="text-sm font-bold tabular-nums text-content">{money(resta)}</span>
                    {isAdmin && (
                      <div className="flex items-center gap-1.5">
                        <QuickPay debtId={d.debt_id} remaining={resta} debtType={d.debt_type} concept={etiqueta} onPaid={load} />
                        <button onClick={() => { setCancelando(d.debt_id); setMotivo(''); }}
                          title="Cancelar este adeudo (pide el motivo y queda en el historial)" aria-label="Cancelar este adeudo"
                          className="rounded-lg border border-line p-1.5 text-content-3 transition hover:border-red-500/50 hover:text-red-600">
                          <XCircle size={15} />
                        </button>
                      </div>
                    )}
                  </div>
                  {cancelando === d.debt_id && (
                    <div className="mt-2 rounded-lg border border-line bg-surface-2 p-2.5">
                      <label htmlFor={`motp-${d.debt_id}`} className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-content-2">Motivo de la cancelación</label>
                      <div className="flex flex-wrap gap-2">
                        <input id={`motp-${d.debt_id}`} autoFocus value={motivo} onChange={(e) => setMotivo(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && motivo.trim()) cancelar(d.debt_id); if (e.key === 'Escape') setCancelando(null); }}
                          placeholder="Ej. lo pagó en efectivo fuera de caja"
                          className="min-w-[12rem] flex-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-content outline-none focus:border-brand-blue" />
                        <button onClick={() => cancelar(d.debt_id)} disabled={!motivo.trim() || saving}
                          className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">Cancelar adeudo</button>
                        <button onClick={() => setCancelando(null)} className="rounded-lg border border-line px-3 py-1.5 text-sm text-content-2">Dejar</button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ── Historial: lo que ha pagado + adeudos cancelados ── */}
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-content-3">Historial</h4>
      {pays === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
      ) : pays.length === 0 && cancelados.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-3 py-4 text-sm text-content-3">Aún no hay pagos a nombre de este maestro.</p>
      ) : (
        <ol className="divide-y divide-line/60 rounded-xl border border-line" data-testid="profe-historial">
          {[
            ...pays.map((p) => ({ tipo: 'pago' as const, fecha: String(p.pay_date || p.datetime || ''), row: p })),
            ...cancelados.map((d: any) => ({ tipo: 'cancel' as const, fecha: String(d.cancelled_at || ''), row: d })),
          ].sort((a, b) => b.fecha.localeCompare(a.fecha)).map((it) => it.tipo === 'pago' ? (
            <li key={`p-${it.row.payment_id}`} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${TYPE_COLOR[tipoDe(it.row)] || TYPE_COLOR.other}`}>{TYPE_LABEL[tipoDe(it.row)] || it.row.type}</span>
                  <MetodoPago metodo={it.row.method} conTexto="sm" size={13} />
                  {it.row.debt_id != null && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 dark:text-emerald-400">a cuenta de adeudo</span>}
                  {(it.row.assoc_name || it.row.comment) && <span className="truncate text-xs text-content-2">{it.row.assoc_name || it.row.comment}</span>}
                </div>
                <div className="mt-0.5 text-xs text-content-3">{fecha(it.fecha)}{it.row.teller ? ` · cobró ${it.row.teller.split(' ')[0]}` : ''}</div>
              </div>
              <span className="text-sm font-semibold tabular-nums text-content">{money(Number(it.row.amount))}</span>
            </li>
          ) : (
            <li key={`c-${it.row.debt_id}`} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 opacity-70">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded bg-surface-2 px-1.5 py-0.5 text-xs font-semibold text-content-3">Adeudo cancelado</span>
                  <span className="truncate text-xs text-content-2">{sinPrefijoVitrina(it.row.comment) || 'Adeudo'}</span>
                </div>
                <div className="mt-0.5 text-xs text-content-3">{fecha(it.fecha)} · {it.row.cancel_reason}{it.row.cancelled_by_name ? ` · ${it.row.cancelled_by_name.split(' ')[0]}` : ''}</div>
              </div>
              <span className="text-sm tabular-nums text-content-3 line-through">{money(Number(it.row.amount))}</span>
            </li>
          ))}
        </ol>
      )}
      <p className="mt-3 flex items-center gap-1.5 text-[11px] text-content-3">
        <ShoppingBag size={12} /> Los fiados del equipo se van acumulando: no entran al efectivo esperado de la caja y no impiden cerrarla.
      </p>
    </div>
  );
}
